import { Divider, Switch } from "@mui/material";
import { useTranslation } from "react-i18next";
import { DesignPriceBrackets } from "../../../types";
import { Box, FlexColBox, CurrencySwitcher } from "../../../common/components";
import { palette, spacing } from "../../../styles/theme";
import { Typography } from "@mui/material";
import { useDistributorPriceCalculator } from "../common/useDistributorPriceCalculator";
import { CalculatePriceModalStyles } from "../common/styles";
import TailoredMessage from "../TailoredMessage";
import { useState, ReactNode } from "react";
import { FormattedPrice } from "../common/types";

interface Props {
  pricing: DesignPriceBrackets;
  notAvailableInCurrency: boolean;
  qty: number;
  hasReachedMaximum: boolean;
  hasMinimum: boolean;
  onToggleDistributorPrices: (checked: boolean) => void;
  ShippingSection: ReactNode;
  shippingPrice: FormattedPrice;
}

const DistributorSection = ({
  pricing,
  qty,
  hasReachedMaximum,
  hasMinimum,
  onToggleDistributorPrices,
  ShippingSection,
  shippingPrice,
}: Props) => {
  const { t } = useTranslation();
  const [showDistributorPrices, setShowDistributorPrices] = useState(false);

  const { distributorMsrp, distributorCost, distributorTotalPrice } =
    useDistributorPriceCalculator(
      pricing.distributor,
      pricing.unitPriceBrackets,
      qty,
      hasReachedMaximum,
      hasMinimum,
      shippingPrice
    );

  return (
    <>
      <Box
        display="flex"
        justifyContent="flex-end"
        alignItems="center"
        gap={"4px"}
      >
        <Typography
          variant="textRegularBold"
          component="label"
          htmlFor="showDistributorPrices"
          sx={{
            cursor: "pointer",
            userSelect: "none",
          }}
        >
          {t("pricing.viewDistributorPrices")}
        </Typography>
        <Switch
          id="showDistributorPrices"
          checked={showDistributorPrices}
          onChange={(_, checked) => {
            setShowDistributorPrices(checked);
            onToggleDistributorPrices(checked);
          }}
        />
      </Box>
      {showDistributorPrices && (
        <FlexColBox
          gap={{ xs: spacing.mediumMobile, lg: spacing.mediumDesktop }}
        >
          <Typography variant="h2" mb="12px">
            {t("pricing.distributorPricing")}
          </Typography>

          {hasReachedMaximum && <TailoredMessage />}

          {!hasReachedMaximum && (
            <>
              <Box sx={CalculatePriceModalStyles.bottomSection}>
                <Typography variant="textRegularBold">
                  {t("pricing.msrp")}
                </Typography>
                <Typography variant="textRegularBold">
                  {distributorMsrp.formatted}
                </Typography>
              </Box>
              <Divider />
              {hasMinimum && !hasReachedMaximum && (
                <>
                  <Box sx={CalculatePriceModalStyles.bottomSection}>
                    <Typography variant="textRegularBold">
                      {t("pricing.distributorCost")}
                    </Typography>
                    <Typography variant="textRegularBold">
                      {distributorCost.formatted}
                    </Typography>
                  </Box>

                  <Divider />

                  {ShippingSection}
                  <Divider />

                  <FlexColBox
                    gap={{ xs: spacing.xsMobile, lg: spacing.xsDesktop }}
                    className={"distributor-total-price"}
                  >
                    <Box sx={CalculatePriceModalStyles.bottomSection}>
                      <Typography variant="textRegularBold">
                        {t("pricing.distributorSubtotal")}
                      </Typography>
                      <Typography variant="textRegularBold">
                        {distributorTotalPrice.formatted}
                      </Typography>
                    </Box>
                    <Box sx={CalculatePriceModalStyles.bottomSection}>
                      <CurrencySwitcher />
                      <Typography
                        variant="textSm"
                        color={palette.grey.nearBlack}
                      >
                        {t("pricing.taxes")}
                      </Typography>
                    </Box>
                  </FlexColBox>

                  <Divider />
                </>
              )}
            </>
          )}
        </FlexColBox>
      )}
    </>
  );
};

export default DistributorSection;
